/**
 * @flow
 */

import React, { useState, useMemo, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { format, addMinutes, startOfDay } from 'date-fns';
import BaseCell from '../../../../components/timetable/Cell';
import withModal from '../../../../lib/withModal';
import { isOverlap } from '../../../../lib/schedule';
import { convertMinutesToDateTime } from '../../../../lib/date';
import { some } from 'lodash';

import CreateAppointmentDialog from '../../containers/CreateAppointmentDialog';

import type { ModalDialogTrigger, Master, Salon } from '../../../../type';

type AppointmentCellProps = {
    time: number,
    salon: Salon,
    master: Master,
    disabled: boolean,
    onModalOpen: Function,
    additionalTime: number,
    date: Object,
    allowToAddAppointments: boolean,
    disabledFirstHalfOfHour: boolean,
    disabledLastHalfOfHour: boolean,
    masterAppointments: boolean,
} & ModalDialogTrigger;

const disabledCss = css`
    background-image: linear-gradient(
        45deg,
        transparent 46%,
        rgba(36, 51, 74, 0.2) 49%,
        rgba(36, 51, 74, 0.2) 51%,
        transparent 55%
    );
    background-size: 8px 8px;
`;

const CellWrapper = styled(BaseCell)`
    flex: 1;
    font-size: 0;
    ${({ disabled }) => disabled && disabledCss};
`;

const DialogTriggerContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const DialogTriggerItem = styled.div`
    display: flex;
    flex: 1;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    transition: background 0.3s, color 0.3s;
    color: transparent;
    user-select: none;
    cursor: pointer;
    min-height: 0;
    + .disabled {
        border-top: 1px solid #e9e9e9;
    }
    &:hover {
        background: #eef8fc;
        color: #35a3d5;
        // if trigger has height on render, timetable markup will break
        font-size: 0.9rem;
    }
`;

const DisabledSpace = styled.div`
    flex: ${({ rowspan }) => rowspan};
    ${disabledCss};
    + .trigger-item {
        border-top: 1px solid #e9e9e9;
    }
`;

const DialogTrigger = ({
    onModalOpen,
    schedule: { format: timeFormat },
    parts,
}) => (
    <DialogTriggerContainer>
        {parts.map(
            ({ startAt, endAt, disabled, rowspan, additionalTime, key }) =>
                disabled ? (
                    <DisabledSpace
                        key={key}
                        rowspan={rowspan}
                        className="disabled"
                    />
                ) : (
                    <DialogTriggerItem
                        key={key}
                        className="trigger-item"
                        onClick={onModalOpen(additionalTime)}
                    >
                        {format(startAt, timeFormat)} -{' '}
                        {format(endAt, timeFormat)}
                    </DialogTriggerItem>
                ),
        )}
    </DialogTriggerContainer>
);

// const DisabledSpace = mapProps(({ rowspan, ...props }) => ({ ...props }))(
//     BaseDisabledSpace,
// );

const AppointmentCell = ({
    time,
    salon,
    provider,
    open,
    onOpen,
    onClose,
    disabled,
    date,
    allowToAddAppointments,
    disabledFirstHalfOfHour,
    disabledLastHalfOfHour,
    masterAppointments,
    fromDate,
    toDate,
    schedule,
    updateAppointments,
    inCabinet,
    ...props
}: AppointmentCellProps) => {
    const [additionalTime, setAdditionalTime] = useState(0);

    const parts = useMemo(() => {
        const { step } = schedule;
        const providerSchedule = provider.schedule;
        const result = [];
        let i = 0;
        for (let t = time; t < time + 60; t += step) {
            const isDisabled = !some(providerSchedule, ({ startAt, endAt }) =>
                isOverlap(startAt, endAt, t, t + step),
            );
            if (
                i > 0 &&
                result[i - 1] &&
                result[i - 1].disabled &&
                isDisabled
            ) {
                result[i - 1].endAt = addMinutes(
                    startOfDay(new Date(date)),
                    t + step,
                );
                result[i - 1].rowspan++;
            } else {
                result.push({
                    startAt: convertMinutesToDateTime(t, date),
                    endAt: convertMinutesToDateTime(t + step, date),
                    additionalTime: t - time,
                    disabled: isDisabled,
                    rowspan: 1,
                    key: i,
                });
                i++;
            }
        }
        return result;
    }, [provider.schedule, date, time, schedule.step]);

    const onModalOpen = useCallback(
        extraTime => () => {
            setAdditionalTime(extraTime);
            onOpen();
        },
        [onOpen],
    );

    return (
        <CreateAppointmentDialog
            trigger={
                <CellWrapper {...props} className="cell" disabled={disabled}>
                    {!disabled && allowToAddAppointments && (
                        <DialogTrigger
                            onModalOpen={onModalOpen}
                            schedule={schedule}
                            parts={parts}
                        />
                    )}
                </CellWrapper>
            }
            open={open}
            onClose={onClose}
            time={time}
            date={date}
            salon={salon}
            id={provider.id}
            additionalTime={additionalTime}
            masterAppointments={masterAppointments}
            updateAppointments={updateAppointments}
            fromDate={fromDate}
            toDate={toDate}
            inCabinet={inCabinet}
        />
    );
};

export default withModal(AppointmentCell);
